import {
    autoInflowsForMode,
    generateTempId,
    mapDestination,
    pruneOrphanedBioscopeMovements,
} from './stateHelpers';
import type { OperateWizardAction, OperateWizardState, WizardDestination } from './types';

export type DestinationAction = Extract<
    OperateWizardAction,
    {
        type:
            | 'addDestination'
            | 'ensureSplitTargets'
            | 'initMergeTargetStub'
            | 'addSplitRemainder'
            | 'removeDestination'
            | 'setDestinationName'
            | 'setDestinationCage'
            | 'setDestinationEstimatedCount'
            | 'setDestinationNotes'
            | 'toggleDestinationInflow'
            | 'setInflowCount';
    }
>;

/** The actions that add, remove or edit destinations and their inflows. */
export function reduceDestinationAction(
    state: OperateWizardState,
    action: DestinationAction
): OperateWizardState {
    switch (action.type) {
        case 'addDestination':
            return {
                ...state,
                destinations: [
                    ...state.destinations,
                    {
                        tempId: generateTempId(),
                        name: '',
                        cageId: null,
                        estimatedFishCount: '',
                        receivesFrom: autoInflowsForMode(state),
                        notes: '',
                    },
                ],
            };

        case 'ensureSplitTargets': {
            // Split mode only — when the operator vacated the source cage
            // (no destination targets it), pre-create enough empty target
            // rows to reach the two-destination minimum the backend requires
            // for `split` inference. Idempotent: noop when a remainder is
            // present, or when there are already ≥2 targets.
            //
            // Triggered from SplitTargetsStep on mount; React strict mode
            // double-firing is harmless because the second call sees ≥2
            // targets and returns the same state reference.
            const initial = state.sources.find((s) => s.fishGroupId === state.initialSourceId);
            const sourceCageId = initial?.currentCageId ?? null;
            const hasRemainder = state.destinations.some((d) => d.cageId === sourceCageId);
            if (hasRemainder) return state;
            const targetCount = state.destinations.length;
            const needed = 2 - targetCount;
            if (needed <= 0) return state;
            const seededTargets: WizardDestination[] = [];
            for (let i = 0; i < needed; i++) {
                seededTargets.push({
                    tempId: generateTempId(),
                    name: '',
                    cageId: null,
                    estimatedFishCount: '',
                    receivesFrom: autoInflowsForMode(state),
                    notes: '',
                });
            }
            return { ...state, destinations: [...state.destinations, ...seededTargets] };
        }

        case 'initMergeTargetStub': {
            // Split-and-merge only — seeds destinations[1] with an empty
            // stub so the Name input on TargetStep has a stable place to
            // dispatch to before a cage is picked. Idempotent: noop if a
            // target already exists, or if the continuation at [0] hasn't
            // been seeded yet (defensive — TargetStep should never mount
            // before SourceContinuationStep has run).
            if (state.destinations.length !== 1) return state;
            const stub: WizardDestination = {
                tempId: generateTempId(),
                name: '',
                cageId: null,
                estimatedFishCount: '',
                receivesFrom: [
                    { sourceFishGroupId: state.initialSourceId, estimatedFishCount: '' },
                ],
                notes: '',
            };
            return { ...state, destinations: [...state.destinations, stub] };
        }

        case 'addSplitRemainder': {
            // Adds a destination pinned to the SOURCE cage that holds the
            // population staying behind after the split. Used by both:
            //   - `split` mode: the "remainder" alongside user-added empty-cage
            //     targets. The backend sees this as another destination, which
            //     also flips the inferred type from the degenerate `merge`
            //     (S=1,D=1) to `split` (S=1,D≥2).
            //   - `split_and_merge` mode: the source's continuation in its own
            //     cage; the merge target is added afterwards by the target step.
            //
            // The remainder always lands at `destinations[0]` so downstream
            // steps can rely on the convention "[0] = continuation,
            // [1] = merge target".
            //
            // Idempotent: noop if a destination already targets the source
            // cage, or if we don't know the source cage.
            const initial = state.sources.find((s) => s.fishGroupId === state.initialSourceId);
            if (!initial || initial.currentCageId == null) return state;
            if (state.destinations.some((d) => d.cageId === initial.currentCageId)) {
                return state;
            }
            const remainder: WizardDestination = {
                tempId: generateTempId(),
                name: '',
                cageId: initial.currentCageId,
                estimatedFishCount: '',
                receivesFrom: [
                    { sourceFishGroupId: state.initialSourceId, estimatedFishCount: '' },
                ],
                notes: '',
            };
            return { ...state, destinations: [remainder, ...state.destinations] };
        }

        case 'removeDestination':
            return pruneOrphanedBioscopeMovements({
                ...state,
                destinations: state.destinations.filter((d) => d.tempId !== action.tempId),
            });

        case 'setDestinationName':
            return mapDestination(state, action.tempId, (d) => ({ ...d, name: action.value }));

        case 'setDestinationCage':
            // Repointing a destination to a different cage invalidates any
            // bioscope movement still tracking the previous cage — the
            // operator picked that cage in `bioscope_movements` as one of
            // the operation's declared destinations, and changing the
            // destination now would otherwise leave a stale movement that
            // ships the bioscope to a cage no longer part of the operation.
            return pruneOrphanedBioscopeMovements(
                mapDestination(state, action.tempId, (d) => ({ ...d, cageId: action.value }))
            );

        case 'setDestinationEstimatedCount':
            return mapDestination(state, action.tempId, (d) => ({
                ...d,
                estimatedFishCount: action.value,
            }));

        case 'setDestinationNotes':
            return mapDestination(state, action.tempId, (d) => ({ ...d, notes: action.value }));

        case 'toggleDestinationInflow':
            return mapDestination(state, action.tempId, (d) => {
                const has = d.receivesFrom.some(
                    (i) => i.sourceFishGroupId === action.sourceFishGroupId
                );
                if (has) {
                    return {
                        ...d,
                        receivesFrom: d.receivesFrom.filter(
                            (i) => i.sourceFishGroupId !== action.sourceFishGroupId
                        ),
                    };
                }
                return {
                    ...d,
                    receivesFrom: [
                        ...d.receivesFrom,
                        {
                            sourceFishGroupId: action.sourceFishGroupId,
                            estimatedFishCount: '',
                        },
                    ],
                };
            });

        case 'setInflowCount':
            return mapDestination(state, action.tempId, (d) => ({
                ...d,
                receivesFrom: d.receivesFrom.map((i) =>
                    i.sourceFishGroupId === action.sourceFishGroupId
                        ? { ...i, estimatedFishCount: action.value }
                        : i
                ),
            }));

        default: {
            const _exhaustive: never = action;
            return _exhaustive;
        }
    }
}
